/**
 * * 新增配置项 `dvDirectionLine` 来配置瀑布图的各个数据项的方向指示线
 *
 * - 仅在 `dvType: 'waterfall'` 时生效
 * - 只考虑竖向瀑布图
 *
 * @example
 * ```javascript
 * {
 *   type: 'bar',
 *   dvType: 'waterfall',
 *   stack: 'waterfall'
 *   dvDirectionLine: {
 *     show: true,
 *     color: 'gray',
 *     direction: 'top'
 *   },
 * }
 * ```
 */
import { type EChartsExtensionInstallRegisters } from 'echarts/lib/extension';
import { type default as ExtensionAPI } from 'echarts/lib/core/ExtensionAPI';
import { Group, makePath, type Rect } from 'echarts/lib/util/graphic';
import {
  initProps,
  updateProps,
  removeElementWithFadeOut
} from 'echarts/lib/animation/basicTransition';
import type { PathProps } from 'zrender/lib/graphic/Path.js';
import type {
  WaterfallSeriesModel,
  WaterfallView,
  RectLayout,
  CustomDataItemOption,
  Element
} from './type';
import { filterWaterfallSeries, eachWaterfall } from './helper';
import { HALF } from './util';

type SVGPath = ReturnType<typeof makePath>;

function buildLines(group: Group, seriesModel: WaterfallSeriesModel) {
  const data = seriesModel.getData();
  const isAnimationEnabled = seriesModel.isAnimationEnabled();
  const globalCfg = seriesModel.get('dvDirectionLine');

  function add(lineEl: Element<SVGPath>) {
    group.add(lineEl);

    if (isAnimationEnabled) {
      lineEl.attr({ style: { opacity: 0 } });

      initProps(lineEl, {}, seriesModel, {
        cb: () => {
          lineEl.attr({ style: { opacity: 1 } });
        }
      });
    }
  }

  function update(lineEl: Element<SVGPath>, opts: PathProps, lineElTmp: Element<SVGPath>) {
    // eslint-disable-next-line no-lonely-if
    if (isAnimationEnabled) {
      lineEl.attr({ rotation: opts.rotation });
      const updateConfig = {
        ...opts,
        shape: { ...lineElTmp.shape } // 复制 shape 而不是 path
      };
      updateProps(lineEl, updateConfig, seriesModel);
    } else {
      lineEl.attr({ ...opts, path: lineElTmp.path });
    }
  }

  // ! 只考虑竖向瀑布图
  data.each(idx => {
    const itemEl = data.getItemGraphicEl(idx) as Rect;
    const itemLayout = data.getItemLayout(idx) as RectLayout;
    const [x0, y0, width, height] = [
      Math.min(itemLayout.x, itemLayout.x + itemLayout.width),
      Math.min(itemLayout.y, itemLayout.y + itemLayout.height),
      Math.abs(itemLayout.width),
      Math.abs(itemLayout.height)
    ];
    const itemModel = data.getItemModel<CustomDataItemOption>(idx);
    const direction = itemModel.get(['dvDirectionLine', 'direction']) ?? globalCfg.direction;
    const color = itemModel.get(['dvDirectionLine', 'color']) ?? globalCfg.color;
    if (itemModel.get(['dvDirectionLine', 'show']) === false || !direction) {
      return;
    }

    // 箭头距离边缘的距离
    const space = 4;
    const arrowW = 6;
    const arrowH = 5;
    const lineWidth = 1;
    // ! 当柱子高度显示不了指向线时隐藏掉
    if (height < space + arrowH) {
      return;
    }

    const pathData = `m ${width / HALF} ${0} l ${-arrowW / HALF} ${arrowH} h ${
      (arrowW - lineWidth) / HALF
    } v ${height - space - arrowH} h ${lineWidth} v ${-(height - space - arrowH)} h ${
      (arrowW - lineWidth) / HALF
    } z`;

    const opts = {
      style: {
        lineWidth: 1,
        fill: color ?? itemEl.style.fill
      },
      rotation: direction === 'top' ? 0 : Math.PI,
      originX: width / HALF,
      originY: (height - space) / HALF,
      x: x0,
      y: y0 + (direction === 'top' ? space : 0),
      z: (data.getItemGraphicEl(idx) as Rect).z + 1
    };
    const name = `dvDirectionLine-${idx}`;
    let lineEl = group.childOfName(name) as Element<SVGPath>;
    const lineElTmp = makePath(
      pathData,
      {
        name,
        ...opts
      },
      {
        width,
        height: height - space,
        x: 0,
        y: 0
      },
      'center'
    );

    if (!lineEl) {
      lineEl = lineElTmp;
      add(lineEl);
    } else {
      update(lineEl, opts, lineElTmp);
    }

    lineEl.__dvAlive = true;
  });
}

function renderDirectionLine(seriesModel: WaterfallSeriesModel, api: ExtensionAPI) {
  const seriesView = api.getViewOfSeriesModel(seriesModel) as WaterfallView;
  let directionLineGroup = seriesView.__dvDirectionLineGroup;
  if (!directionLineGroup) {
    directionLineGroup = new Group({ name: 'dvDirectionLine' });
    seriesView.__dvDirectionLineGroup = directionLineGroup;
    seriesView.group.add(directionLineGroup);
  }

  directionLineGroup.eachChild((childEl: Element<SVGPath>) => {
    childEl.__dvAlive = false;
  });

  if (seriesModel.get('dvDirectionLine')?.show) {
    // add & update
    buildLines(directionLineGroup, seriesModel);
  }

  // remove
  directionLineGroup
    .children()
    .slice()
    .forEach((childEl: Element<SVGPath>) => {
      if (!childEl.__dvAlive) {
        removeElementWithFadeOut(childEl, seriesModel);
      }
    });
}

export function install(registers: EChartsExtensionInstallRegisters) {
  registers.registerUpdateLifecycle('series:afterupdate', (ecModal, api) => {
    eachWaterfall(
      filterWaterfallSeries(ecModal.getSeries() as unknown as WaterfallSeriesModel[]),
      seriesModels => seriesModels.forEach(seriesModel => renderDirectionLine(seriesModel, api))
    );
  });
}
